import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import "./styles/styles.css";
import { AuthProvider, useAuth } from "./context/AuthContext"; // ✅ Ensure AuthContext is used
import ProtectedRoute from "./ProtectedRoute"; // ✅ Import Protected Route
import Sidebar from "./deck/Sidebar";
import Deck from "./deck/Deck";
import Analytics from "./analytics/Analytics";
import Profiling from "./profiling/Profiling";
import DetailProfile from "./profiling/DetailProfile";
import Notam from "./notam/notam";
import Originator from "./origin tracker/OTCardContainer";
import Graph from "./graph/Graph";
import AdminPanel from "./admin panel/adminpanel";
import LocationSearch from "./globe/LocationSearch";
import CommentMonitoring from "./comment-monitoring/CommentMonitoring";
import Login from "./Login/temp/Login";
import NewsBulletin from "./deck/NewsBulletin";
import DARKHAWK from "./darkhawk/DARKHAWK";
import Super from "./SuperAdmin/SuperAdmin";
// import "./background.css"; 
// import Background from "./background"; // ✅ Import Background Component
import { logUserAction } from './logUserAction'; // Import the logging function
import LockedOut from "./Login/LockedOut";  // Import the new LockedOut page




/* ✅ 404 Not Found Component */
const NotFound = () => (
  <div className="not-found">
    <h2>🚫 404 - Page Not Found</h2>
    <p>Oops! The page you are looking for does not exist.</p>
    <a href="/deck" className="go-home">Go Back Home</a>
  </div>
);

/* ✅ Layout with Sidebar - Ensures Sidebar Loads Immediately */
const Layout = () => {
  const location = useLocation();
  const { token, loading } = useAuth(); // ✅ Fetch token and loading state

  const isLoginPage = location.pathname === "/login";

  // ✅ Log page visit
  useEffect(() => {
    if (!isLoginPage && token) {
      // Assuming 'user' is stored in localStorage or global state with the username
      const username = JSON.parse(localStorage.getItem('user'))?.username;  // Adjust based on your storage structure
      
      // Ensure the username is available before calling logUserAction
      if (username) {
        logUserAction(username, location.pathname, 'View', { path: location.pathname }, 'Page visited');
      }
    }
  }, [location, isLoginPage, token]);
  

  // ✅ Show loading message while checking authentication
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="app">
       {/* <Background /> */}
        {/* Background Video */}
        <video autoPlay muted loop className="video-background">
        <source src="/icons/bg3.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    
      {!isLoginPage && token && <Sidebar />} {/* ✅ Sidebar only appears after login */}
      <div className="scroll-container">
        <Outlet />
      </div>
    </div>
  );
};

/* ✅ Main App Component */
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar
          closeOnClick
          pauseOnHover
          draggable
          limit={3}
          pauseOnFocusLoss={false}
          theme="light"
        />

        <Routes>
          {/* ✅ Default Route: Redirect based on authentication state */}
          <Route path="/" element={<AuthRedirect />} />

          {/* ✅ Public Route: Login Page */}
          <Route path="/login" element={<Login />} />

          {/* ✅ Protected Routes (Requires Login) */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route path="/deck" element={<Deck />} />
              <Route path="/DetailProfile/:platform/:username" element={<DetailProfile />} />
              <Route path="/comment-monitoring" element={<CommentMonitoring />} />
              <Route path="/Analytics" element={<Analytics />} />
              <Route path="/notam" element={<Notam />} />
              <Route path="/profiling" element={<Profiling />} />
              <Route path="/originator" element={<Originator />} />
              <Route path="/graph" element={<Graph />} />
              <Route path="/adminpanel" element={<AdminPanel />} />
              <Route path="/location-search" element={<LocationSearch />} />
              <Route path="/news-bulletin" element={<NewsBulletin />} />
              <Route path="/superadmin" element={<Super />} />
              <Route path="/darkhawk" element={<DARKHAWK />} />
              <Route path="/locked-out" element={<LockedOut />} />
            </Route>
          </Route>

          {/* ✅ Catch-all for unknown routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

/* ✅ Auth Redirect: Wait for authentication before redirecting */
const AuthRedirect = () => {
  const { token, loading } = useAuth(); // ✅ Fetch token and loading state

  if (loading) {
    return <div className="loading-screen">Checking authentication...</div>;
  }

  return token ? <Navigate to="/deck" replace /> : <Navigate to="/login" replace />;
};

export default App;
